import React from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useContactForm from '../hooks/useContactForm';

const INPUT_CLASS = "w-full bg-transparent border-b border-black/30 px-0 py-4 text-black placeholder:text-black/50 focus:border-black focus:outline-none transition-colors duration-300";

const STATUS_MESSAGES = {
  success: {
    text: 'Message sent successfully!',
    className: 'text-green-400',
    testId: 'contact-success-message'
  },
  validation: {
    text: 'Please check your details and try again (a valid email address is required).',
    className: 'text-red-400',
    testId: 'contact-validation-message'
  },
  error: {
    text: 'Failed to send message. Please try again.',
    className: 'text-red-400',
    testId: 'contact-error-message'
  }
};

const StatusMessage = ({ status }) => {
  const config = STATUS_MESSAGES[status];
  if (!config) return null;

  return (
    <motion.p
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${config.className} text-sm`}
      data-testid={config.testId}
    >
      {config.text}
    </motion.p>
  );
};

const ContactForm = () => {
  const { t } = useLanguage();
  const { formData, isSubmitting, submitStatus, handleChange, handleSubmit } = useContactForm();

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder={t('contact.form.name')}
          required
          className={INPUT_CLASS}
          data-testid="contact-input-name"
        />
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder={t('contact.form.email')}
          required
          className={INPUT_CLASS}
          data-testid="contact-input-email"
        />
      </div>
      <input
        type="text"
        name="subject"
        value={formData.subject}
        onChange={handleChange}
        placeholder={t('contact.form.subject')}
        required
        className={INPUT_CLASS}
        data-testid="contact-input-subject"
      />
      <textarea
        name="message"
        value={formData.message}
        onChange={handleChange}
        placeholder={t('contact.form.message')}
        required
        rows={4}
        className={`${INPUT_CLASS} resize-none`}
        data-testid="contact-input-message"
      />

      <StatusMessage status={submitStatus} />

      <button
        type="submit"
        disabled={isSubmitting}
        className="group flex items-center gap-3 px-8 py-4 bg-black text-white uppercase tracking-[0.15em] text-xs font-medium hover:bg-black/80 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="contact-submit-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Sending...
          </>
        ) : (
          <>
            {t('contact.form.send')}
            <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
